import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { baseQueryWithReauth } from './baseQuery';

interface ITableListResponse {
  tables: string[];
}

const utilityApi = createApi({
  reducerPath: 'utilityApi',
  baseQuery: baseQueryWithReauth,
  endpoints: builder => ({
    getWorkingTables: builder.query<BackendResponse<ITableListResponse>, void>({
      query: () => '/utility/working-tables',
    }),
    getMasterTableNames: builder.query<
      BackendResponse<ITableListResponse | string[]>,
      void
    >({
      query: () => '/master/master-table-names',
    }),
    getSampleCsvForTable: builder.query<Blob, string>({
      query: (tableName: string) => ({
        url: `/utility/generate-csv/${tableName}`,
        responseHandler: response => response.blob(),
      }),
      transformResponse: response => {
        if (response instanceof Blob) {
          return response;
        }
        throw new Error('Unexpected response type');
      },
    }),
    getMasterTableSampleCsv: builder.query<Blob, string>({
      query: (tableName: string) => ({
        url: '/master/master-table-sample-download',
        params: { tableName },
        responseHandler: response => response.blob(),
      }),
      transformResponse: response => {
        if (response instanceof Blob) {
          return response;
        }
        throw new Error('Unexpected response type');
      },
    }),
    bulkUploadCsv: builder.mutation<void, { file: File; tableName: string }>({
      query: ({ file, tableName }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('tableName', tableName);
        return {
          url: '/utility/bulk-upload/csv',
          method: 'POST',
          body: formData,
        };
      },
    }),
    masterTableUploadCsv: builder.mutation<
      void,
      { file: File; tableName: string }
    >({
      query: ({ file, tableName }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('tableName', tableName);
        return {
          url: '/master/master-table-upload',
          method: 'POST',
          body: formData,
        };
      },
    }),
    executeQuery: builder.mutation<
      BackendResponse<{ message: string; data: any[] }>,
      { query: string }
    >({
      query: (payload) => ({
        url: '/common/execute-query',
        method: 'POST',
        body: payload,
      }),
    }),
    getExecuteQueryLogs: builder.query<
      BackendResponse<{ items: any[]; total: number; page: number; pageSize: number; totalPages: number }>,
      { page: number; pageSize: number; search?: string; fromDate?: string; toDate?: string; sortBy?: string }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.fromDate) queryParams.append('fromDate', params.fromDate);
        if (params.toDate) queryParams.append('toDate', params.toDate);
        if (params.sortBy) queryParams.append('sortBy', params.sortBy);
        
        return {
          url: `/common/execute-query/logs?${queryParams.toString()}`,
          method: 'GET',
        };
      },
    }),
  }),
});

export const {
  useLazyGetSampleCsvForTableQuery,
  useLazyGetMasterTableSampleCsvQuery,
  useGetWorkingTablesQuery,
  useGetMasterTableNamesQuery,
  useBulkUploadCsvMutation,
  useMasterTableUploadCsvMutation,
  useExecuteQueryMutation,
  useGetExecuteQueryLogsQuery,
} = utilityApi;

export default utilityApi;
